import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { JsonLd } from "@/components/JsonLd";
import { FaqAccordion } from "@/components/FaqAccordion";
import { KeyTakeaways } from "@/components/KeyTakeaways";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SITE } from "@/content/site";
import { breadcrumbSchema } from "@/lib/seo";
import { bnPageHead } from "@/lib/bn-head";
import { faqSchema } from "@/lib/schema";

const PATH = "/bn/online-marketing-small-businesses";
const TITLE = "ছোট ব্যবসার জন্য অনলাইন মার্কেটিং — প্রশ্নোত্তর ও শুরুর গাইড";
const CRUMBS = [
  { name: "হোম", path: "/bn" },
  { name: "ছোট ব্যবসার অনলাইন মার্কেটিং", path: PATH },
];

const TAKEAWAYS = [
  "অনলাইন মার্কেটিং মানে সার্চ, সোশ্যাল, ইমেইল ও এআই-এর মাধ্যমে ক্রেতার কাছে পৌঁছানো — যা মাপা যায়, পরীক্ষা করা যায় এবং দরকারে কমানো-বাড়ানো যায়।",
  "বেশিরভাগ ছোট ব্যবসার উচিত দ্রুত ফলের জন্য একটি পেইড চ্যানেল আর দীর্ঘমেয়াদি ফলের জন্য একটি নিজস্ব চ্যানেল (এসইও বা ইমেইল) দিয়ে শুরু করা — একসঙ্গে ছয়টি নয়।",
  "শুরুর বাজেট হিসেবে আয়ের ৫–১০% ধরা যায়, যা বিজ্ঞাপন খরচ আর কনটেন্ট, পেজ ও ট্র্যাকিং তৈরির কাজে ভাগ হয়।",
  "ওয়েবসাইট আছে এমন প্রায় সব ছোট ব্যবসার জন্য এসইও লাভজনক, কারণ বিজ্ঞাপনের মতো টাকা দেওয়া বন্ধ করলেও এটি ট্রাফিক আনতে থাকে।",
];

const FAQS = [
  { q: "ছোট ব্যবসা অনলাইনে কীভাবে মার্কেটিং করবে?", a: "একজন ক্রেতা আর একটি ফল দিয়ে শুরু করুন — 'বেশি ট্রাফিক' নয়, বরং 'মাসে ৩০টি অর্ডার জিজ্ঞাসা'-র মতো নির্দিষ্ট কিছু। তারপর ওয়েবসাইট ঠিক করুন (গতি, মোবাইল, পরিষ্কার দাম, কাজ করা ফর্ম), দ্রুত ফলের জন্য একটি পেইড চ্যানেল আর দীর্ঘমেয়াদি ফলের জন্য এসইও বা ইমেইলের মতো একটি নিজস্ব চ্যানেল বাছুন, আর এক টাকা খরচের আগেই মাপার ব্যবস্থা করুন। ৯০ দিনের চক্রে কাজ করুন: চালু করুন, মাপুন, যা কাজ করেনি তা বাদ দিন। বেশিরভাগ উদ্যোগ ব্যর্থ হয় খুব তাড়াতাড়ি চ্যানেল বদলানোর কারণে।" },
  { q: "অনলাইন মার্কেটিংয়ে কত টাকা খরচ করা উচিত?", a: "সাধারণ মানদণ্ড হলো মোট আয়ের ৫–১০%, যদিও নতুন ব্যবসা গতি তৈরিতে বেশি খরচ করে। বাংলাদেশের একটি ছোট ব্যবসার জন্য মাসে ২৫,০০০–৬০,০০০ টাকা দিয়ে শুরু করা যায়, যা বিজ্ঞাপন আর কনটেন্টে ভাগ হয়। বিজ্ঞাপনের খরচ আর কৌশল ও কাজের ফি আলাদাভাবে হিসাব হয়। আসল প্রশ্ন মোট অঙ্ক নয়, বরং প্রতিটি টাকা কোন গ্রাহক আনল তা বলা যায় কিনা। পূর্ণ প্যাকেজ দাম পেজে দেখুন।" },
  { q: "ছোট ব্যবসার জন্য সবচেয়ে ভালো অনলাইন মার্কেটিং কৌশল কী?", a: "একক 'সেরা' কৌশল নেই — আপনার ক্রেতা, বাজেট ও সময়ের ওপর নির্ভর করে। এই মাসেই বিক্রি দরকার হলে পেইড সার্চ বা সোশ্যাল অ্যাড সবচেয়ে দ্রুত। ৪–৬ মাস অপেক্ষা করতে পারলে এসইও সময়ের সঙ্গে সবচেয়ে কম খরচে ফল দেয়। গ্রাহকের তালিকা থাকলে ইমেইল ও এসএমএস সবচেয়ে দ্রুত খরচ ফেরত দেয়। মূল কথা: দুটি চ্যানেল বাছুন, মনোযোগ দিয়ে মাপুন, আর প্রথম দুটি লাভজনক না হওয়া পর্যন্ত তৃতীয়টি যোগ করবেন না।" },
  { q: "অনলাইনে ব্যবসার বিজ্ঞাপন কীভাবে দেব?", a: "অনলাইন বিজ্ঞাপন মূলত তিন ধরনের: পেইড সার্চ (Google Ads), পেইড সোশ্যাল (Facebook, TikTok, LinkedIn) এবং ডিসপ্লে বা ভিডিও। স্থানীয় ব্যবসার জন্য আপনার শহরে আপনার সার্ভিস খোঁজা মানুষের কাছে Google Ads সাধারণত সবচেয়ে ভালো প্রথম ধাপ। পণ্যভিত্তিক ব্যবসার জন্য Facebook ও TikTok অ্যাড এমন মানুষের কাছে পৌঁছায় যারা এখনো খুঁজছে না কিন্তু আপনার ক্রেতার মতো। একটি প্ল্যাটফর্ম, ছোট দৈনিক বাজেট আর একটি লক্ষ্য দিয়ে শুরু করুন; ৩০ দিনের মধ্যে যে অ্যাডের খরচ লাভের চেয়ে বেশি, সেটি বন্ধ করুন।" },
  { q: "ছোট ব্যবসার জন্য এসইও কি লাভজনক?", a: "ওয়েবসাইট আছে এমন প্রায় সব ছোট ব্যবসার জন্য, হ্যাঁ। বিজ্ঞাপনের মতো নয়, টাকা দেওয়া বন্ধ করলেও এসইও ট্রাফিক আসতে থাকে — কেনার উদ্দেশ্যের কীওয়ার্ডে র‍্যাংক করা একটি পেজ বছরের পর বছর গ্রাহক আনতে পারে। লোকাল এসইও (Google Business Profile, সাইটেশন, রিভিউ) বিশেষভাবে লাভজনক, কারণ এটি সরাসরি ম্যাপ থেকে কল ও ভিজিট আনে। সমস্যা হলো সময় — অর্থবহ ফল আসতে ৩–৬ মাস লাগে, তাই শুরুতে একটি পেইড চ্যানেলের সঙ্গে চালানো ভালো।" },
];

const RELATED = [
  { t: "এসইও সার্ভিস", d: "কীওয়ার্ড রিসার্চ, অন-পেজ, টেকনিক্যাল সমাধান ও অথরিটি তৈরি।", to: "/bn/services/seo" },
  { t: "প্যাকেজ ও দাম", d: "এসইও, AEO ও GEO প্যাকেজে কী থাকে আর কত খরচ।", to: "/bn/pricing" },
  { t: "ফ্রি এআই অ্যাকশন প্ল্যান", d: "আপনার ওয়েবসাইটের লেখা থেকে অগ্রাধিকারসহ প্ল্যান — বাংলায়।", to: "/bn/ai-action-plan" },
  { t: "ফ্রি এসইও অডিট", d: "যেকোনো সাইটের তাৎক্ষণিক এসইও স্ন্যাপশট।", to: "/bn/free-seo-audit" },
  { t: "কেস স্টাডি", d: "আসল ক্লায়েন্টের কাজ ও ফলাফল।", to: "/bn/case-studies" },
  { t: "শহর অনুযায়ী এসইও", d: "রংপুর, ঢাকা, চট্টগ্রামসহ বিভিন্ন শহরের লোকাল এসইও।", to: "/bn/seo-services" },
] as const;

export const Route = createFileRoute("/bn/online-marketing-small-businesses")({
  head: () =>
    bnPageHead({
      path: PATH,
      enPath: "/online-marketing-small-businesses",
      title: TITLE,
      desc: "ছোট ব্যবসার মালিকরা অনলাইন মার্কেটিং নিয়ে যে পাঁচটি প্রশ্ন সবচেয়ে বেশি করেন — কীভাবে শুরু করবেন, কত খরচ, কোন কৌশল, কীভাবে বিজ্ঞাপন দেবেন আর এসইও লাভজনক কিনা। সহজ বাংলায় উত্তর।",
      keywords: ["online marketing bangla", "ছোট ব্যবসার অনলাইন মার্কেটিং", "small business marketing bangladesh", "এসইও কি লাভজনক"],
      type: "article",
    }),
  component: BnOnlineMarketingPage,
});

function BnOnlineMarketingPage() {
  return (
    <>
      <Breadcrumbs items={CRUMBS} />
      <Section headingLevel={1} eyebrow="গাইড" title={<>ছোট ব্যবসার জন্য <span className="text-gradient">অনলাইন মার্কেটিং।</span></>}>
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          ছোট ব্যবসার মালিকদের সবচেয়ে বেশি করা পাঁচটি প্রশ্ন — সহজ বাংলায়, আসল সংখ্যাসহ, বাড়তি কথা ছাড়া।
        </p>
      </Section>

      <div className="container-x">
        <KeyTakeaways items={TAKEAWAYS} />
      </div>

      <Section eyebrow="প্রশ্নোত্তর" title={<>মালিকদের সবচেয়ে বেশি করা <span className="text-gradient">পাঁচটি প্রশ্ন।</span></>}>
        <Reveal>
          <FaqAccordion items={FAQS} idPrefix="bn-faq-online-marketing" />
        </Reveal>
        <JsonLd data={faqSchema(FAQS)} />
      </Section>

      <Section eyebrow="সংশ্লিষ্ট রিসোর্স" title={<>সার্ভিস, টুল ও <span className="text-gradient">প্রমাণ।</span></>}>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {RELATED.map((r) => (
            <Link key={r.to} to={r.to} className="group flex h-full flex-col rounded-3xl border border-border bg-card p-7 hover:border-primary/40 transition">
              <h3 className="font-display text-xl leading-snug">{r.t}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{r.d}</p>
              <span className="mt-auto pt-5 inline-flex items-center gap-1 text-sm font-medium text-primary">দেখুন <ArrowUpRight size={15} /></span>
            </Link>
          ))}
        </div>
      </Section>

      <Section className="!pt-0">
        <div className="rounded-[2rem] p-8 sm:p-12 lg:p-16 text-center bg-card border border-border">
          <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-2xl mx-auto">
            আপনার ব্যবসার জন্য <span className="text-gradient">আলাদা প্ল্যান চান?</span>
          </h2>
          <p className="mt-5 max-w-lg mx-auto text-sm text-muted-foreground">
            কী বিক্রি করেন আর কোথায় — জানান। এক কর্মদিবসের মধ্যে সৎ মতামত দেব কোন চ্যানেল আপনার জন্য কাজের।
          </p>
          <Link to="/bn/contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium">
            স্ট্র্যাটেজি কল বুক করুন <ArrowUpRight size={16} />
          </Link>
        </div>
      </Section>

      <JsonLd data={breadcrumbSchema(CRUMBS)} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Article",
        headline: TITLE,
        inLanguage: "bn-BD",
        author: { "@type": "Person", "@id": `${SITE.url}/#person`, name: SITE.fullName, url: `${SITE.url}/bn/about` },
        url: `${SITE.url}${PATH}`,
      }} />
    </>
  );
}
